// Compact Bridge Setup card — status pills instead of device/camera meta.
// Presentational and pure so the setup layout can snapshot-test without the depot.
// Per rule: think-in-systems (ai.knowledge/global) — props in, JSX out.
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Room } from '@studio/models/room';
import { getCurrentVendorBadgeIcon } from '@studio/design-system/themes/vendor.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { permissionLevelLabel } from '../permission-level.js';
import type { StudioPickerDescriptor } from '../types.js';

export interface SetupStudioCardProps {
  readonly descriptor: StudioPickerDescriptor;
  readonly disambiguator?: string | undefined;
  readonly disabled?: boolean;
  readonly linking?: boolean;
  readonly dimmed?: boolean;
  readonly selectionOnly?: boolean;
  readonly computerName: string;
  readonly onSelect: (roomId: Room['id']) => void;
  readonly onRetryCapacity?: ((roomId: Room['id']) => void) | undefined;
}

const StatusPill = ({
  dotClassName,
  pillClassName,
  label
}: {
  readonly dotClassName: string;
  readonly pillClassName: string;
  readonly label: string;
}): ReactElement => (
  <span
    className={twMerge(
      'inline-flex h-[22px] shrink-0 items-center gap-6 whitespace-nowrap rounded-full px-[9px] text-[11px] font-medium tracking-[0.02em]',
      pillClassName
    )}
  >
    <span className={twMerge('size-6 rounded-full', dotClassName)} />
    {label}
  </span>
);

const LockIcon = (): ReactElement => (
  <svg aria-hidden='true' className='size-[12px] shrink-0 fill-current' viewBox='0 0 10.5 11.5'>
    <path d='M 5.25,0 C 4.3883388,0 3.5604622,0.3418815 2.9511719,0.95117188 2.3418815,1.5604622 2,2.3883388 2,3.25 V 4.5 H 1.75 C 0.79238405,4.5 0,5.2923841 0,6.25 v 3.5 c 0,0.957621 0.79240215,1.75 1.75,1.75 h 7 c 0.9576031,0 1.75,-0.792397 1.75,-1.75 V 6.25 C 10.5,5.2924022 9.7076212,4.5 8.75,4.5 H 8.5 V 3.25 C 8.5,2.3883388 8.1581185,1.5604622 7.5488281,0.95117188 6.9395378,0.3418815 6.1116612,0 5.25,0 Z m 0,1.5 C 5.714416,1.5 6.1598936,1.683331 6.4882812,2.0117188 6.816669,2.3401064 7,2.785584 7,3.25 V 4.5 H 3.5 V 3.25 C 3.5,2.785584 3.6833311,2.3401064 4.0117188,2.0117188 4.3401065,1.683331 4.785584,1.5 5.25,1.5 Z M 1.75,6 h 1 5 1 C 8.8969766,6 9,6.10304 9,6.25 v 3.5 C 9,9.8969947 8.8969947,10 8.75,10 h -7 C 1.60304,10 1.5,9.8969766 1.5,9.75 V 6.25 C 1.5,6.1030581 1.6030581,6 1.75,6 Z' />
  </svg>
);

/** Compact setup-flow studio card with Not linked / Linked / Multibridge status. */
export const SetupStudioCard = ({
  descriptor,
  disambiguator,
  disabled = false,
  linking = false,
  dimmed = false,
  selectionOnly = false,
  computerName,
  onSelect,
  onRetryCapacity
}: SetupStudioCardProps): ReactElement => {
  const { room, bridgeStatus, canLinkBridge, desktopBridgeCapacity, deviceCount, roles } =
    descriptor;
  const roleLabel = permissionLevelLabel(roles);
  // Occupied or not, a false canLinkBridge still needs the lock line — the
  // card stays in the list so the user can open the permission overlay.
  const needsPermission = !selectionOnly && canLinkBridge === false;
  const atCapacity =
    !selectionOnly && !needsPermission && desktopBridgeCapacity?.kind === 'at_capacity';
  const capacityUnknown =
    !selectionOnly && !needsPermission && desktopBridgeCapacity?.kind === 'unknown';
  const capacityMaximum =
    desktopBridgeCapacity?.kind === 'at_capacity' ? desktopBridgeCapacity.maximum : undefined;
  const interactive =
    !disabled &&
    !linking &&
    !dimmed &&
    !atCapacity &&
    (!capacityUnknown || onRetryCapacity !== undefined);

  let status: ReactElement;
  if (selectionOnly) {
    status = (
      <StatusPill dotClassName='bg-focus' pillClassName='bg-primary/20 text-focus' label='Open' />
    );
  } else if (atCapacity) {
    status = (
      <StatusPill
        dotClassName='bg-warning'
        pillClassName='bg-warning/20 text-warning'
        label='At capacity'
      />
    );
  } else if (capacityUnknown) {
    status = (
      <StatusPill
        dotClassName='bg-secondary'
        pillClassName='bg-secondary/20 text-muted'
        label='Unknown'
      />
    );
  } else if (bridgeStatus?.kind === 'multibridge') {
    status = (
      <StatusPill
        dotClassName='bg-accent'
        pillClassName='bg-accent/20 text-accent'
        label={`${bridgeStatus.machineNames.length} bridges`}
      />
    );
  } else if (bridgeStatus?.kind === 'linked') {
    status = (
      <StatusPill dotClassName='bg-focus' pillClassName='bg-primary/20 text-focus' label='Linked' />
    );
  } else {
    status = (
      <StatusPill
        dotClassName='bg-secondary'
        pillClassName='bg-secondary/20 text-muted'
        label='Not linked'
      />
    );
  }

  let subtitle: ReactElement | undefined;
  if (linking) {
    subtitle = (
      <span
        className='mt-auto flex items-center gap-8 text-[12px] leading-[1.35] text-light'
        {...DataSTag(Sel.StudioPicker, 'studio_linking', room.id)}
      >
        <span
          aria-hidden='true'
          className='border-white/20 size-[12px] shrink-0 animate-spin rounded-full border-2 border-t-focus'
        />
        Connecting {computerName} to {room.name}…
      </span>
    );
  } else if (needsPermission) {
    subtitle = (
      <span className='mt-auto flex items-center gap-6 text-[12px] text-secondary'>
        <LockIcon />
        Needs owner or admin
      </span>
    );
  } else if (atCapacity) {
    subtitle = (
      <span className='mt-auto text-[12px] leading-[1.35] text-secondary'>
        {deviceCount} of {capacityMaximum} bridges linked · can&apos;t add another
      </span>
    );
  } else if (capacityUnknown) {
    subtitle = (
      <span className='mt-auto text-[12px] leading-[1.35] text-secondary'>
        Bridge count unavailable{' '}
        <span
          className='ml-4 font-demibold text-focus'
          {...DataSTag(Sel.StudioPicker, 'capacity_retry', room.id)}
        >
          Retry
        </span>
      </span>
    );
  } else if (bridgeStatus?.kind === 'linked') {
    subtitle = (
      <span className='mt-auto text-[12px] leading-[1.35] text-muted'>
        Linked to {bridgeStatus.machineName}
      </span>
    );
  } else if (bridgeStatus?.kind === 'multibridge' && bridgeStatus.machineNames.length > 0) {
    subtitle = (
      <span className='mt-auto text-[12px] leading-[1.35] text-muted'>
        {bridgeStatus.machineNames.join(', ')}
      </span>
    );
  }

  return (
    <button
      type='button'
      // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — same card stag as workspace cards.
      {...STagNew(Sel.StudioPicker, 'studio_card', room.id)}
      // aria-label replaces the content-derived name, so repeat the role for screen readers.
      // The duplicate-name qualifier is announced here only; the card never shows it.
      aria-label={
        capacityUnknown
          ? `Retry bridge capacity for ${room.name}`
          : disambiguator === undefined
            ? undefined
            : [room.name, roleLabel, disambiguator].filter(Boolean).join(', ')
      }
      disabled={!interactive}
      onClick={(): void => {
        if (capacityUnknown) {
          onRetryCapacity?.(room.id);
          return;
        }
        onSelect(room.id);
      }}
      className={twMerge(
        'flex min-h-[104px] flex-col gap-8 rounded-8 bg-base-100 p-12 text-left text-light',
        'border border-white/[0.06] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]',
        'transition-[background-color,border-color,opacity] duration-[120ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
        'hover:enabled:bg-base-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/50',
        linking && 'border-primary',
        atCapacity && 'opacity-[0.72]',
        // Arbitrary opacity: the token scale has no 30 / 70 (studio.client tokens).
        dimmed && 'pointer-events-none opacity-[0.3]',
        needsPermission && !linking && 'opacity-[0.72]',
        !interactive && 'cursor-default'
      )}
    >
      <span className='flex items-start justify-between gap-8'>
        {/* The thumbnail is the product mark, not a letter fallback (ENG-4483
            ITB finding #005). Resolved through the vendor helper so Hive ships
            its own badge — per rule: ui-architecture (ai.knowledge/global). */}
        <span
          {...DataSTag(Sel.StudioPicker, 'studio_avatar', room.id)}
          className='flex size-[34px] shrink-0 items-center justify-center overflow-hidden rounded-6 bg-base-50 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]'
        >
          <img
            aria-hidden='true'
            alt=''
            className='size-[22px] object-contain'
            src={getCurrentVendorBadgeIcon()}
          />
        </span>
        {status}
      </span>

      <span className='line-clamp-2 text-[14px] font-medium leading-[1.3] tracking-[-0.01em]'>
        {room.name}
      </span>

      {roleLabel !== undefined && (
        <span
          className='truncate text-[11px] font-medium text-secondary'
          {...DataSTag(Sel.StudioPicker, 'studio_role', room.id)}
        >
          {roleLabel}
        </span>
      )}

      {subtitle}
    </button>
  );
};
